import * as actionTypes from "@/store/action-types";
import config from "@/api/config";
import environments from "@/shared/constants/environments";
import {
  PADDLE_BILLING_EVENTS,
  PADDLE_BILLING_SCRIPT_SELECTOR,
  PADDLE_BILLING_SDK_URL,
  PADDLE_CHECKOUT_DISPLAY_MODES,
  PADDLE_CHECKOUT_THEMES,
  PADDLE_ENVIRONMENTS,
  PADDLE_INLINE_FRAME_HEIGHT,
  PADDLE_PROVIDERS
} from "@/shared/constants/pricing/paddle";
import { syncPlanSubscription } from "@/api/plan";

// Keeps Paddle Billing SDK initialization shared across pricing/register components.
let paddleBillingInitialized = false;
// Routes the active Paddle Billing checkout events back to the component that opened it.
let paddleBillingEventHandler = null;

export default {
  data() {
    return {
      paddleBillingInitialized: false,
      paddleBillingScriptPromise: null,
      subscriptionStatus: {
        interval: null,
        timeout: null,
        intervalCounter: 0,
        success: false,
        error: false,
      },
      mixin_user: {
        id: null,
        token: null
      },
      // Tracks whether this instance owns the shared Billing event handler, so unmount
      // only clears it when it would otherwise fire into a destroyed component.
      ownsPaddleBillingEventHandler: false,
    };
  },
  beforeUnmount() {
    this.planMixin_resetSubscriptionStatus();
    if (this.ownsPaddleBillingEventHandler) {
      paddleBillingEventHandler = null;
      this.ownsPaddleBillingEventHandler = false;
    }
  },
  methods: {
    // Loads Paddle Billing v2 only when a Billing checkout is needed.
    async planMixin_loadPaddleBillingScript() {
      if (typeof window === 'undefined') return false

      if (this.paddleBillingScriptPromise) {
        return this.paddleBillingScriptPromise
      }

      this.paddleBillingScriptPromise = new Promise((resolve) => {
        const existingScript = document.querySelector(
          PADDLE_BILLING_SCRIPT_SELECTOR
        )
        if (existingScript) {
          if (window.Paddle?.Initialize) {
            resolve(true)
            return
          }
          existingScript.addEventListener('load', () => resolve(true), { once: true })
          existingScript.addEventListener('error', () => resolve(false), { once: true })
          return
        }

        const script = document.createElement('script')
        script.src = PADDLE_BILLING_SDK_URL
        script.async = true
        script.dataset.paddleBilling = 'true'
        script.onload = () => resolve(true)
        script.onerror = () => resolve(false)
        document.head.appendChild(script)
      })

      return this.paddleBillingScriptPromise
    },

    // Initializes Paddle Billing with the client token and environment from UI config.
    async planMixin_initializePaddleBilling() {
      if (typeof window === 'undefined') {
        return false
      }

      const loaded = await this.planMixin_loadPaddleBillingScript()
      if (!loaded || !window.Paddle) {
        console.error('Paddle Billing is not loaded')
        return false
      }

      if (paddleBillingInitialized) {
        this.paddleBillingInitialized = true
        return true
      }

      const clientToken = config?.paddle?.billing?.clientToken
      if (!clientToken) {
        console.error('Paddle Billing client token is missing from config')
        return false
      }

      if (
        window.Paddle.Environment &&
        config?.paddle?.billing?.environment !== environments.PRODUCTION
      ) {
        window.Paddle.Environment.set(PADDLE_ENVIRONMENTS.SANDBOX)
      }

      window.Paddle.Initialize({
        token: clientToken,
        eventCallback: (event) => {
          if (paddleBillingEventHandler) {
            paddleBillingEventHandler(event)
          }
        }
      })

      paddleBillingInitialized = true
      this.paddleBillingInitialized = true
      return true
    },

    // Opens Paddle Billing checkout for the given plan.
    async planMixin_subscribePlan(plan, userData, callback = null, closeCallback = null, options = {}) {
      this.planMixin_resetSubscriptionStatus();
      return this.planMixin_subscribeBillingPlan(plan, userData, callback, closeCallback, options)
    },

    // Opens Paddle Billing checkout and starts polling once payment completes.
    async planMixin_subscribeBillingPlan(plan, userData, callback = null, closeCallback = null, options = {}) {
      let checkoutCompleted = false
      let callbackHandled = false

      const clearEventHandler = () => {
        paddleBillingEventHandler = null
        this.ownsPaddleBillingEventHandler = false
      }

      const finishCheckout = (success) => {
        if (callbackHandled) return
        callbackHandled = true
        clearEventHandler()
        if (callback) callback(success)
      }

      paddleBillingEventHandler = (event) => {
        const eventName = event?.name || event?.type || event?.event
        if (config.environment !== environments.PRODUCTION) {
          console.debug('Paddle Billing checkout event:', eventName, event)
        }

        const completedEvents = [
          PADDLE_BILLING_EVENTS.CHECKOUT_COMPLETED,
          PADDLE_BILLING_EVENTS.CHECKOUT_PAYMENT_COMPLETED
        ]
        if (completedEvents.includes(eventName)) {
          if (checkoutCompleted) return
          checkoutCompleted = true
          clearEventHandler()
          // Close the overlay ourselves instead of waiting for Paddle's own
          // "Transaction completed successfully" screen to auto-dismiss - that
          // leaves our own "confirming your subscription" screen stuck behind
          // it for a few unexplained seconds. Safe to call here: checkout.closed
          // fires as a result, but paddleBillingEventHandler is already cleared
          // above, so it's a no-op rather than being treated as a cancellation.
          window.Paddle?.Checkout?.close()
          // For inline checkouts, close() removes the iframe from the caller's
          // frameTarget div immediately, which would otherwise sit empty for
          // however long polling below takes. Let the caller show its own
          // loading state right now instead of waiting for the callback.
          if (options.onPaymentConfirmed) options.onPaymentConfirmed()
          this.planMixin_pollingSubscriptionStatus(finishCheckout, plan.publicId)
          return
        }

        if (
          eventName === PADDLE_BILLING_EVENTS.CHECKOUT_CLOSED &&
          !checkoutCompleted
        ) {
          clearEventHandler()
          this.planMixin_resetSubscriptionStatus()
          if (closeCallback) closeCallback()
          return
        }

        if (
          eventName === PADDLE_BILLING_EVENTS.CHECKOUT_ERROR ||
          eventName === PADDLE_BILLING_EVENTS.CHECKOUT_PAYMENT_FAILED
        ) {
          this.planMixin_resetSubscriptionStatus()
          finishCheckout(false)
        }
      }
      this.ownsPaddleBillingEventHandler = true

      const initialized = await this.planMixin_initializePaddleBilling()
      if (!initialized) {
        finishCheckout(false)
        return false
      }

      const settings = options.settings || {
        displayMode:
          options.displayMode || PADDLE_CHECKOUT_DISPLAY_MODES.OVERLAY,
        theme: PADDLE_CHECKOUT_THEMES.LIGHT,
      }

      const checkoutConfig = {
        items: [
          {
            priceId: plan.priceId || plan.id,
            quantity: 1
          }
        ],
        customer: {
          email: userData?.email
        },
        customData: {
          user_id: userData?.id,
          plan_public_id: plan.publicId,
          provider: PADDLE_PROVIDERS.BILLING
        },
        settings
      }

      if (options.inline) {
        checkoutConfig.settings = {
          ...settings,
          displayMode: PADDLE_CHECKOUT_DISPLAY_MODES.INLINE,
          frameTarget: options.frameTarget,
          frameInitialHeight: options.frameInitialHeight || PADDLE_INLINE_FRAME_HEIGHT,
          frameStyle: options.frameStyle
        }
      }

      this.$nextTick(() => window.Paddle.Checkout.open(checkoutConfig))
      return true
    },

    // Syncs the plan from Paddle on early ticks only; later ticks rely on the webhook having landed.
    async planMixin_maybeSyncExpectedPlan(expectedPlanPublicId, attemptNumber, maxAttempts) {
      if (!expectedPlanPublicId || attemptNumber > maxAttempts) return;
      await syncPlanSubscription(expectedPlanPublicId, this.mixin_user.token);
    },

    // Makes one last sync/refresh attempt once the polling limit is reached.
    async planMixin_finalizePollingAttempt(expectedPlanPublicId, expectedState, callback) {
      try {
        if (expectedPlanPublicId) {
          try {
            await syncPlanSubscription(expectedPlanPublicId, this.mixin_user.token)
          } catch (err) {
            console.error("Final sync error:", err);
          }
        }

        const userSynced = await this.planMixin_syncLocalUser(expectedState);
        if (userSynced) {
          this.subscriptionStatus.error = false;
          this.subscriptionStatus.success = true;
          this.planMixin_handleSubscription(false);
          if (callback) callback(true);
          return;
        }
      } finally {
        if (this.subscriptionStatus.success) return;
        this.planMixin_handleSubscription(true);
        if (callback) callback(false);
      }
    },

    // Polls until webhook/sync creates the expected local subscription state.
    planMixin_pollingSubscriptionStatus(callback = null, expectedPlanPublicId = null, expectedState = null) {
      this.planMixin_resetSubscriptionStatus();
      this.subscriptionStatus.intervalCounter = 0;
      this.subscriptionStatus.success = false;
      this.subscriptionStatus.error = false;

      const pollingInterval = 4000;
      const intervalLimit = 15; // Increased to allow backend time to sync with Octopus
      const maxSyncAttempts = 3;

      // Simple delay before starting to give backend time to process webhook and sync with Octopus
      this.subscriptionStatus.timeout = setTimeout(() => {
        this.subscriptionStatus.timeout = null;
        // Start polling at regular intervals
        this.subscriptionStatus.interval = setInterval(async () => {
          this.subscriptionStatus.intervalCounter++;

          try {
            await this.planMixin_maybeSyncExpectedPlan(
              expectedPlanPublicId,
              this.subscriptionStatus.intervalCounter,
              maxSyncAttempts
            );

            // Sync user data to check the subscription status
            const userSynced = await this.planMixin_syncLocalUser(expectedState);

            // If user data is successfully synced and subscription exists
            if (userSynced) {
              clearInterval(this.subscriptionStatus.interval);
              this.subscriptionStatus.success = true;
              this.planMixin_handleSubscription(false);
              if (callback) callback(true);
              return;
            }
          } catch (err) {
            console.error("Polling error:", err);
          }

          // Check if polling limit is reached
          if (this.subscriptionStatus.intervalCounter >= intervalLimit) {
            clearInterval(this.subscriptionStatus.interval);
            this.subscriptionStatus.error = true;
            await this.planMixin_finalizePollingAttempt(expectedPlanPublicId, expectedState, callback);
          }
        }, pollingInterval);
      }, 3000); // 3 second delay before starting polling
    },

    // Refreshes local user data and optionally validates the expected subscription state.
    async planMixin_syncLocalUser(expectedState = null) {
      try {
        // Store token for authentication (like old logic)
        if (this.mixin_user.token) {
          localStorage.setItem('token', JSON.stringify(this.mixin_user.token))
        }

        const userId = this.mixin_user.id || this.user?.id
        const response = await this.$store.dispatch(actionTypes.FETCH_USER, {
          userId,
        });

        const user = response?.data?.data;

        // Remove token after fetching (like old logic)
        if (this.mixin_user.token) {
          localStorage.removeItem('token')
        }

        if (!user) return false;

        await this.$store.dispatch(actionTypes.UPDATE_USER_LOCAL, user);
        localStorage.setItem("user", JSON.stringify(user));

        if (typeof expectedState === 'function') {
          return Boolean(expectedState(user));
        }

        return Boolean(user?.plan?.subscription);
      } catch (error) {
        // Remove token on error (like old logic)
        if (this.mixin_user.token) {
          localStorage.removeItem('token')
        }
        return false;
      }
    },

    // Switches which plan a trialing user is trialing (Bronze/Silver/Gold) - no checkout,
    // no billing, just repoints default_plan_id server-side, then refreshes local user state
    // so pricing cards/limits reflect it immediately.
    async planMixin_switchTrialPlan(planId) {
      await this.$store.dispatch(actionTypes.SWITCH_TRIAL_PLAN, { planId });
      await this.planMixin_syncLocalUser();
    },

    // Handle subscription status update
    planMixin_handleSubscription(isError) {
      if (!isError) {
        this.subscriptionStatus.success = true;
      } else {
        this.subscriptionStatus.error = true;
      }
    },

    // Clears pending polling timers before starting or ending a checkout flow.
    planMixin_resetSubscriptionStatus() {
      if (this.subscriptionStatus.timeout) {
        clearTimeout(this.subscriptionStatus.timeout);
      }

      if (this.subscriptionStatus.interval) {
        clearInterval(this.subscriptionStatus.interval);
      }

      this.subscriptionStatus = {
        interval: null,
        timeout: null,
        intervalCounter: 0,
        success: false,
        error: false,
      };
    },

    // Get plan features for user
    async planMixin_getPlanFeatures(userId) {
      try {
        const response = await this.$store.dispatch(actionTypes.FETCH_USER, {
          userId,
        });
        return response?.data?.data;
      } catch (error) {
        throw error;
      }
    },
  },
};
